import { Environment } from '@util/Environment';
import 'firebase/compat/remote-config';
import { App } from 'hydra-data-types';
import { MarkViewedOn } from './Experiment';
import { FlowConfigExperiment } from './FlowConfigExperiment';
import { RemoteConfigExperiment } from './RemoteConfigExperiment';

namespace Variant {
  export const Dev = {
    A: 'VariantA',
    B: 'VariantB',
  } as const;

  /**
   * Non-referred Nerva flow pool that is randomly assigned. Default (Control) is the only assignable
   * non-referred variant, so 100% of D2C users get it.
   * String values must stay disjoint from `NervaReferredConfig` for `select()` maps.
   */
  export const NervaNonReferredConfig = {
    Default: 'Default',
  } as const;

  /** Referred (HCP) Nerva flow pool. */
  export const NervaReferredConfig = {
    ReferredPersonalise: 'ReferredPersonalise',
  } as const;
}

type NervaConfigVariants = typeof Variant.NervaNonReferredConfig &
  typeof Variant.NervaReferredConfig;

/**
 * Configure the active experiments. This should be done in conjunction with configuring the related experiments in
 * Firebase RemoteConfig. See the Dev experiment for an example of naming conventions etc.
 *
 * This Notion page has more detailed information:
 * https://www.notion.so/mindsethealth/Setting-up-Split-Test-Experiments-0102de5390c04668bee6d4111ee86516
 */
export const AllExperiments = {
  Dev: new RemoteConfigExperiment({
    name: 'DevExperiment',
    variants: Variant.Dev,
    default: Variant.Dev.A,
    activeApps: [App.Nerva],
    markViewedOn: Environment.DEVELOPMENT ? MarkViewedOn.Init : MarkViewedOn.None,
  }),

  NervaConfig: new FlowConfigExperiment<NervaConfigVariants>({
    name: 'NervaConfig',
    nonReferredVariants: Variant.NervaNonReferredConfig,
    referredVariants: Variant.NervaReferredConfig,
    default: Variant.NervaNonReferredConfig.Default,
    activeApps: [App.Nerva],
    markViewedOn: MarkViewedOn.None,
    // Specs opt into the referred config via explicit overrides.
    excludeFromIntegrationTests: true,
  }),
} as const;

export const ActiveExperimentList = Object.values(AllExperiments).filter(
  (experiment) => experiment.isActive
);
